import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, LogOut, PanelLeftClose, PanelLeftOpen, Settings, Star } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { isOperatorExperience } from '@/auth/permissions';
import { buildDetail, buildLabel } from '@/lib/buildInfo';
import { cn } from '@/lib/utils';
import { useGuideOptional } from '@/features/guide/useGuide';
import { NAV_BADGE_LABELS, useNavBadges } from '@/app/hooks/useNavBadges';
import { isRouteItem, navItemVisible, type NavItem } from './navItems';
import {
  activeNavItem, allNavItems, footerWorkspaces, mainWorkspaces, workspaceBadges, workspaceForPath,
  workspaceHeading, workspaceHome, workspaceItems,
  type NavWorkspace,
} from './navWorkspaces';
import { useNavPanelCollapsed } from './useNavPrefs';
import { useNavPins } from './useNavPins';

/**
 * The desktop sidebar — seven destinations (2026-09-25).
 *
 * Michael: "I'd cut this from 29 sidebar links to 7 main destinations. The
 * sidebar should answer 'Where am I working?' Each page can hold the related
 * tools." So the open sidebar is ONE row per workspace — Today · Projects ·
 * Field · Equipment · Purchasing · People & Safety · Reports — with the
 * area's waiting work summed onto the row ("Purchasing · 6"). The pages
 * inside an area are its tab strip (AreaTabs) — the top row beside this
 * sidebar since 2026-10-10 — not here.
 *
 * Above the seven: the person's own PINS (useNavPins — Time Entry for someone
 * entering time daily, Invoices for someone processing them), saved to the
 * account. Below: the Tools footer, the person's row with sign out, and the
 * build stamp.
 *
 * Collapsed, it is a 56px strip of one icon per workspace, and hovering or
 * focusing an icon shows that area's pages in a flyout, so the strip is
 * never a dead end. Two things are marked and must not look alike: the AREA
 * you are in (the row, aria-current="true") and — in a flyout — the PAGE you
 * are on (aria-current="page").
 */

const HEADING = 'px-3 pb-0.5 pt-3 text-[10px] font-semibold uppercase tracking-wider text-graphite-500';

export function Sidebar() {
  const location = useLocation();
  const { user, logout, can } = useAuth();
  const { collapsed, toggle } = useNavPanelCollapsed();
  const badges = useNavBadges();
  const { pins } = useNavPins();
  // Help opens the Guide RootLayout mounts. Optional: the sidebar is unit-
  // tested on its own, so the hook returns null rather than throwing there.
  const guide = useGuideOptional();

  // Operator experience (2026-09-17): one workspace, My job, and sign out.
  // The filter is navWorkspacesFor() — the phone reads the same one.
  const experience = user?.experience;
  const operator = isOperatorExperience(user);
  const sections = useMemo(() => mainWorkspaces(can, experience), [can, experience]);
  const tools = useMemo(() => footerWorkspaces(can, experience).flatMap((w) => workspaceItems(w, can)), [can, experience]);
  const currentWorkspaceKey = workspaceForPath(location.pathname, experience)?.key;
  const currentItem = activeNavItem(location.pathname, experience);

  // Pins resolve against the configuration: a route that is no longer in the
  // nav, or one this person may not open, is simply not shown.
  const pinnedItems = useMemo(() => {
    if (operator) return [];
    const byRoute = new Map(allNavItems().filter(isRouteItem).map((i) => [i.to, i] as const));
    return pins.map((to) => byRoute.get(to)).filter((i): i is NavItem => !!i && navItemVisible(i, can));
  }, [pins, can, operator]);

  // Which flyout is open while collapsed: a section key, 'tools', or none.
  // Navigating anywhere closes it, and so does opening the sidebar back up.
  const [flyout, setFlyout] = useState<string | null>(null);
  useEffect(() => { setFlyout(null); }, [location.pathname, collapsed]);
  const rootRef = useRef<HTMLElement>(null);
  // Escape hands focus back to the icon; that focus must not reopen the
  // flyout it just closed.
  const skipNextFocusOpen = useRef(false);
  useEffect(() => {
    if (!flyout) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setFlyout(null);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [flyout]);
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && flyout) {
      e.stopPropagation();
      const trigger = rootRef.current?.querySelector<HTMLElement>('[aria-expanded="true"]');
      setFlyout(null);
      if (trigger) { skipNextFocusOpen.current = true; trigger.focus(); }
    }
  }

  const initials = user?.name?.split(' ').map((n) => n[0]).join('').slice(0, 2) || 'U';

  /** A count pill, shared by workspace rows and page rows. */
  function pill(count: number, label: string) {
    if (count <= 0) return null;
    return (
      <span
        aria-label={label}
        className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-safety-500 px-1.5 text-[11px] font-semibold leading-5 text-white"
      >
        {count}
      </span>
    );
  }

  /** The label row every kind of page entry shares, so icons and text line up. */
  function inner(item: NavItem, compact = false) {
    const Icon = item.icon;
    const count = item.badge ? badges[item.badge] ?? 0 : 0;
    return (
      <>
        <Icon className={cn('shrink-0', compact ? 'h-4 w-4' : 'h-[18px] w-[18px]')} aria-hidden />
        <span className="truncate">{item.label}</span>
        {item.external && (
          <>
            {!compact && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-graphite-500" aria-hidden />}
            <span className="sr-only">{item.hint ?? '(opens the NADC portal)'}</span>
          </>
        )}
        {item.badge && pill(count, NAV_BADGE_LABELS[item.badge]?.(count) ?? `${count} waiting`)}
      </>
    );
  }

  /** One page row: a route Link, a portal <a>, or the Help button. */
  function row(item: NavItem, compact = false) {
    const active = currentItem?.to === item.to;
    const className = cn(
      'relative flex w-full items-center rounded-md text-left transition-colors',
      compact ? 'gap-1.5' : 'gap-2.5',
      compact ? 'px-1.5 py-1 text-[11px]' : 'py-[5px] pl-3 pr-2 text-[13px]',
      active
        ? 'bg-safety-500/10 font-medium text-safety-500 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[3px] before:rounded-r before:bg-safety-500'
        : 'text-graphite-300 hover:bg-graphite-800 hover:text-graphite-100',
    );
    if (item.external) {
      return <li key={item.to}><a href={item.to} className={className}>{inner(item, compact)}</a></li>;
    }
    if (item.action === 'guide') {
      return (
        <li key={item.to}>
          <button type="button" onClick={() => guide?.setOpen(true)} className={className}>{inner(item, compact)}</button>
        </li>
      );
    }
    return (
      <li key={item.to}>
        <Link to={item.to} aria-current={active ? 'page' : undefined} className={className}>{inner(item, compact)}</Link>
      </li>
    );
  }

  /**
   * One DESTINATION row: the workspace's icon and name, the sum of the
   * waiting work inside it, and aria-current="true" while you are anywhere
   * in the area. It goes to the area's home page.
   */
  function destination(workspace: NavWorkspace) {
    const home = workspaceHome(workspace, can);
    if (!home) return null;
    const Icon = workspace.icon;
    const current = workspace.key === currentWorkspaceKey;
    const keys = workspaceBadges(workspace, can);
    const count = keys.reduce((n, k) => n + (badges[k] ?? 0), 0);
    const countLabel = keys
      .filter((k) => (badges[k] ?? 0) > 0)
      .map((k) => NAV_BADGE_LABELS[k]?.(badges[k]) ?? `${badges[k]} waiting`)
      .join(', ');
    return (
      <li key={workspace.key}>
        <Link
          to={home}
          aria-current={current ? 'true' : undefined}
          className={cn(
            'relative flex w-full items-center gap-3 rounded-md py-2 pl-3 pr-2 text-sm transition-colors',
            current
              ? 'bg-safety-500/10 font-medium text-safety-500 before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[3px] before:rounded-r before:bg-safety-500'
              : 'text-graphite-200 hover:bg-graphite-800 hover:text-graphite-50',
          )}
        >
          <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
          <span className="truncate">{workspace.label}</span>
          {pill(count, countLabel)}
        </Link>
      </li>
    );
  }

  /**
   * A collapsed-strip icon with its flyout. The button is the trigger (hover,
   * focus, or click — click only ever opens); the flyout sits to its right
   * and holds the area's pages, so nothing is reachable in one mode and not
   * the other. `align` puts the footer's flyout on the bottom edge.
   */
  function strip(workspace: NavWorkspace, items: NavItem[], current: boolean, align: 'top' | 'bottom' = 'top') {
    const { key, label, icon: Icon } = workspace;
    const open = flyout === key;
    const keys = workspaceBadges(workspace, can);
    const count = keys.reduce((n, k) => n + (badges[k] ?? 0), 0);
    return (
      <li
        key={key}
        className="relative"
        onMouseEnter={() => setFlyout(key)}
        onMouseLeave={() => setFlyout((f) => (f === key ? null : f))}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFlyout((f) => (f === key ? null : f)); }}
      >
        <button
          type="button"
          aria-label={count > 0 ? `${label} (${count} waiting)` : label}
          title={label}
          aria-expanded={open}
          aria-current={current ? 'true' : undefined}
          onClick={() => setFlyout(key)}
          onFocus={() => { if (skipNextFocusOpen.current) { skipNextFocusOpen.current = false; return; } setFlyout(key); }}
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
            current
              ? 'bg-safety-500/15 text-safety-500 before:absolute before:-left-2 before:top-1.5 before:h-7 before:w-1 before:rounded-r before:bg-safety-500'
              : open
                ? 'bg-graphite-800 text-graphite-100'
                : 'text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100',
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
          {count > 0 && (
            <span aria-hidden className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-safety-500 px-1 text-center text-[10px] font-bold leading-4 text-white">
              {count}
            </span>
          )}
        </button>
        {open && (
          <div className={cn('absolute left-full z-50 pl-1', align === 'top' ? 'top-0' : 'bottom-0')}>
            <div
              role="group"
              aria-label={`${label} pages`}
              className="w-52 rounded-lg border border-graphite-700 bg-graphite-950 py-1 shadow-xl"
            >
              <p className={cn(HEADING, 'pt-1.5')}>{workspaceHeading(workspace)}</p>
              <ul className="space-y-px px-1.5 pb-1">{items.map((i) => row(i, true))}</ul>
            </div>
          </div>
        )}
      </li>
    );
  }

  const toggleButton = (
    <button
      type="button"
      onClick={toggle}
      aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
      aria-expanded={!collapsed}
      className="flex h-8 w-8 items-center justify-center rounded-md text-graphite-500 transition-colors hover:bg-graphite-800 hover:text-graphite-100"
    >
      {collapsed ? <PanelLeftOpen className="h-4 w-4" aria-hidden /> : <PanelLeftClose className="h-4 w-4" aria-hidden />}
    </button>
  );

  // ---- collapsed: the icon strip ------------------------------------------
  if (collapsed) {
    const toolsWorkspace = footerWorkspaces(can, experience)[0];
    return (
      <nav
        ref={rootRef}
        aria-label="Main"
        onKeyDown={onKeyDown}
        className="flex w-14 shrink-0 flex-col items-center border-r border-graphite-800 bg-graphite-950 py-2"
      >
        <div className="mb-2">{toggleButton}</div>
        {pinnedItems.length > 0 && (
          <ul className="mb-2 flex flex-col items-center gap-1 border-b border-graphite-800 pb-2" aria-label="Pinned">
            {pinnedItems.map((item) => {
              const Icon = item.icon;
              const active = currentItem?.to === item.to;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-label={item.label}
                    title={`${item.label} (pinned)`}
                    aria-current={active ? 'page' : undefined}
                    className={cn('flex h-9 w-9 items-center justify-center rounded-lg transition-colors', active ? 'bg-safety-500/15 text-safety-500' : 'text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100')}
                  >
                    <Icon className="h-[18px] w-[18px]" aria-hidden />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        <ul className="flex flex-col items-center gap-1">
          {sections.map((w) => strip(w, workspaceItems(w, can), w.key === currentWorkspaceKey))}
        </ul>
        <ul className="mt-auto flex flex-col items-center gap-1">
          {toolsWorkspace && strip(toolsWorkspace, tools, currentWorkspaceKey === toolsWorkspace.key, 'bottom')}
          {!operator && (
            <li>
              <Link to="/settings" aria-label="Settings" title="Settings" className="flex h-10 w-10 items-center justify-center rounded-lg text-graphite-400 transition-colors hover:bg-graphite-800 hover:text-graphite-100">
                <Settings className="h-5 w-5" aria-hidden />
              </Link>
            </li>
          )}
          <li>
            <button type="button" onClick={() => logout()} aria-label="Sign out" title="Sign out" className="flex h-10 w-10 items-center justify-center rounded-lg text-graphite-400 transition-colors hover:bg-graphite-800 hover:text-graphite-100">
              <LogOut className="h-5 w-5" aria-hidden />
            </button>
          </li>
        </ul>
      </nav>
    );
  }

  // ---- open: pins, destinations, footer, you --------------------------------
  return (
    <nav
      ref={rootRef}
      aria-label="Main"
      className="flex w-[232px] shrink-0 flex-col border-r border-graphite-800 bg-graphite-950"
    >
      {/* As tall as the top row beside it, so the two borders meet. */}
      <div className="flex h-12 items-center justify-between border-b border-graphite-800/60 pl-4 pr-2 [@media(pointer:coarse)]:h-14">
        {/* The NADC mark (Michael, 2026-10-12: the logo left with the shared
            top bar). As tall as the wordmark's capitals — Inter's are 0.727 of
            the font size — and standing on its baseline; decorative, so the
            link still reads "DemoOps". */}
        <Link to="/" className="flex items-baseline gap-1.5 text-lg font-bold tracking-tight text-safety-500">
          <img src="/nadc-logo-mark.png" alt="" className="h-[0.727em] w-auto" />
          DemoOps
        </Link>
        {toggleButton}
      </div>

      <div className="flex-1 overflow-y-auto pb-3">
        {pinnedItems.length > 0 && (
          <section aria-label="Pinned pages" data-testid="pinned-block">
            <h2 className={cn(HEADING, 'flex items-center gap-1.5')}>
              <Star className="h-3 w-3 fill-current" aria-hidden /> Pinned
            </h2>
            <ul className="space-y-px px-2">{pinnedItems.map((i) => row(i))}</ul>
          </section>
        )}
        <section aria-label="Workspaces">
          {pinnedItems.length > 0 && <h2 className={HEADING}>Go to</h2>}
          <ul className={cn('space-y-px px-2', pinnedItems.length === 0 && 'pt-2')}>
            {sections.map(destination)}
          </ul>
        </section>
      </div>

      {tools.length > 0 && (
        <div className="border-t border-graphite-800 px-2 pb-1 pt-2" aria-label="Tools" role="group">
          <ul className="grid grid-cols-2 gap-x-1 gap-y-px">{tools.map((i) => row(i, true))}</ul>
        </div>
      )}

      <div className="flex items-center gap-2.5 border-t border-graphite-800 px-3 py-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-graphite-700 text-[11px] font-medium text-graphite-200" aria-hidden>
          {initials}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-graphite-200">{user?.name || 'User'}</span>
        <button
          type="button"
          onClick={() => logout()}
          aria-label="Sign out"
          title="Sign out"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-graphite-500 transition-colors hover:bg-graphite-800 hover:text-graphite-100"
        >
          <LogOut className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* Build stamp (2026-09-25): commit count + short SHA; the tooltip
          carries the branch and build time. */}
      <div
        className="border-t border-graphite-800/60 px-3 py-1.5 font-mono text-[10px] text-graphite-500"
        title={buildDetail() || undefined}
        data-testid="build-stamp"
      >
        {buildLabel()}
      </div>
    </nav>
  );
}
